// LoginFormOverlay.js - 登录/注册表单（HTML 覆盖层，支持键盘操作）

const STYLE_ID = 'login-form-overlay-styles';

const STYLES = `
.login-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    pointer-events: none;
    font-family: monospace;
}
.login-overlay * {
    box-sizing: border-box;
    font-family: monospace;
}
.login-panel {
    pointer-events: auto;
    width: min(420px, 92vw);
    padding: 28px 32px 24px;
    background: rgba(10, 12, 28, 0.94);
    border: 2px solid #4cc9f0;
    box-shadow: 0 0 24px rgba(76, 201, 240, 0.25), inset 0 0 40px rgba(0, 0, 0, 0.4);
}
.login-panel-title {
    margin: 0 0 4px;
    text-align: center;
    font-size: 22px;
    color: #4cc9f0;
    letter-spacing: 4px;
}
.login-panel-subtitle {
    margin: 0 0 20px;
    text-align: center;
    font-size: 12px;
    color: #888888;
}
.login-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
}
.login-tab {
    flex: 1;
    padding: 10px 0;
    border: 1px solid #333355;
    background: #1a1a2e;
    color: #aaaaaa;
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.login-tab:hover {
    border-color: #4cc9f0;
    color: #ffffff;
}
.login-tab.active {
    border-color: #4cc9f0;
    background: rgba(76, 201, 240, 0.15);
    color: #4cc9f0;
    font-weight: bold;
}
.login-field {
    margin-bottom: 14px;
}
.login-field label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    color: #cccccc;
}
.login-field input {
    width: 100%;
    padding: 10px 12px;
    font-size: 15px;
    color: #ffffff;
    background: #1e1e36;
    border: 1px solid #3a3a5c;
    border-radius: 0;
    outline: none;
    transition: border-color 0.15s;
}
.login-field input:focus {
    border-color: #4cc9f0;
    box-shadow: 0 0 0 1px rgba(76, 201, 240, 0.4);
}
.login-field.hidden {
    display: none;
}
.login-status {
    min-height: 20px;
    margin: 8px 0 16px;
    font-size: 13px;
    text-align: center;
    color: #aaaaaa;
}
.login-status.error { color: #ff6666; }
.login-status.success { color: #66ff99; }
.login-status.loading { color: #ffcc66; }
.login-submit {
    width: 100%;
    padding: 12px;
    font-size: 16px;
    font-weight: bold;
    color: #0a0a18;
    background: #4cc9f0;
    border: none;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;
}
.login-submit:hover {
    background: #6dd4f5;
}
.login-submit:active {
    transform: scale(0.98);
}
.login-submit:disabled {
    background: #3a5a6a;
    color: #888888;
    cursor: not-allowed;
    transform: none;
}
.login-hint {
    margin: 14px 0 0;
    text-align: center;
    font-size: 11px;
    color: #666688;
}
`;

export default class LoginFormOverlay {
    constructor(scene, callbacks = {}) {
        this.scene = scene;
        this.onSubmit = callbacks.onSubmit || (() => {});
        this.onModeChange = callbacks.onModeChange || (() => {});
        this.mode = 'login';
        this.root = null;
        this.elements = {};
        this._onKeyDown = null;
    }

    create() {
        if (!document.getElementById(STYLE_ID)) {
            const style = document.createElement('style');
            style.id = STYLE_ID;
            style.textContent = STYLES;
            document.head.appendChild(style);
        }

        this.root = document.createElement('div');
        this.root.className = 'login-overlay';
        this.root.innerHTML = `
            <div class="login-panel">
                <h1 class="login-panel-title">账号登录</h1>
                <p class="login-panel-subtitle">登录后即可同步存档与多人游戏</p>
                <div class="login-tabs">
                    <button type="button" class="login-tab active" data-mode="login">登录</button>
                    <button type="button" class="login-tab" data-mode="register">注册</button>
                </div>
                <div class="login-field">
                    <label for="login-username">用户名</label>
                    <input id="login-username" type="text" autocomplete="username" placeholder="3-20 个字符" maxlength="20" />
                </div>
                <div class="login-field">
                    <label for="login-password">密码</label>
                    <input id="login-password" type="password" autocomplete="current-password" placeholder="至少 6 个字符" />
                </div>
                <div class="login-field hidden" data-field="email">
                    <label for="login-email">邮箱（选填）</label>
                    <input id="login-email" type="email" autocomplete="email" placeholder="用于找回账号（可选）" />
                </div>
                <p class="login-status" data-role="status">请输入账号信息</p>
                <button type="button" class="login-submit" data-role="submit">进入游戏</button>
                <p class="login-hint">Enter 确认 · Tab 切换字段</p>
            </div>
        `;
        document.body.appendChild(this.root);

        this.elements = {
            title: this.root.querySelector('.login-panel-title'),
            tabs: this.root.querySelectorAll('.login-tab'),
            username: this.root.querySelector('#login-username'),
            password: this.root.querySelector('#login-password'),
            emailField: this.root.querySelector('[data-field="email"]'),
            email: this.root.querySelector('#login-email'),
            status: this.root.querySelector('[data-role="status"]'),
            submit: this.root.querySelector('[data-role="submit"]')
        };

        this.elements.tabs.forEach((tab) => {
            tab.addEventListener('click', () => this.setMode(tab.dataset.mode));
        });

        this.elements.submit.addEventListener('click', () => this._handleSubmit());

        this._onKeyDown = (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                this._handleSubmit();
            }
        };
        this.root.addEventListener('keydown', this._onKeyDown);

        this.setMode('login');

        this.scene.time.delayedCall(100, () => {
            this.elements.username?.focus();
        });
    }

    setMode(mode) {
        this.mode = mode === 'register' ? 'register' : 'login';
        const isRegister = this.mode === 'register';

        this.elements.title.textContent = isRegister ? '创建账号' : '账号登录';
        this.elements.submit.textContent = isRegister ? '注册账号' : '进入游戏';
        this.elements.emailField.classList.toggle('hidden', !isRegister);
        this.elements.password.autocomplete = isRegister ? 'new-password' : 'current-password';

        this.elements.tabs.forEach((tab) => {
            tab.classList.toggle('active', tab.dataset.mode === this.mode);
        });

        this.onModeChange(this.mode);
    }

    getValues() {
        return {
            username: this.elements.username.value.trim(),
            password: this.elements.password.value,
            email: this.elements.email.value.trim()
        };
    }

    setStatus(message, type = 'info') {
        this.elements.status.textContent = message;
        this.elements.status.className = `login-status ${type}`;
    }

    setLoading(loading) {
        this.elements.submit.disabled = loading;
        this.elements.username.disabled = loading;
        this.elements.password.disabled = loading;
        this.elements.email.disabled = loading;
        this.elements.tabs.forEach((tab) => {
            tab.disabled = loading;
        });
    }

    _handleSubmit() {
        if (this.elements.submit.disabled) {
            return;
        }
        const values = this.getValues();
        this.onSubmit(this.mode, values);
    }

    destroy() {
        if (this.root) {
            this.root.removeEventListener('keydown', this._onKeyDown);
            this.root.remove();
            this.root = null;
        }
        this.elements = {};
    }
}
